import { apiClient } from 'axios-client';
import { format } from 'date-fns';
import { Formik, useFormikContext } from 'formik';
import { archiveViews } from 'generated/api-client';
import { ProFormArchiveModalQuery, ProViewSetInput } from 'generated/graphql';
import { useCurrentBilikPerson } from 'hooks/use-current-bilik-person/use-current-bilik-person';
import { ProFormUpdateFields } from 'pages/pros/pro-form/pro-form-update-fields-type';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { equals } from 'remeda';
import { ToastError, ToastSuccess } from 'utils';
import { flattenObject } from 'utils/flatten-object';
import { getFormErrorMessage } from 'utils/form-error-message';
import * as Yup from 'yup';
import {
  ProFormArchiveFields,
  ProFormArchiveModalProps,
} from './pro-form-archive-modal-repository';
import ProFormArchiveModalView from './pro-form-archive-modal-view';

type ProFormArchiveModalContainerProps = ProFormArchiveModalProps & {
  proViews?: NonNullable<
    ProFormArchiveModalQuery['company']
  >['proPresentations'][0]['proViews'];
  proViewId: number;
  companyId?: number;
  updateProView: (
    proViewIds: number[],
    set: ProViewSetInput,
  ) => Promise<number[] | undefined>;
  refetch: () => void;
};

const ProFormArchiveModalContainer: FunctionComponent<
  ProFormArchiveModalContainerProps
> = ({ proViewId, proViews, updateProView, onClose, refetch, ...rest }) => {
  const {
    values: proFormValues,
    setFieldValue,
    validateForm,
    handleSubmit,
  } = useFormikContext<ProFormUpdateFields>();

  const { currentBilikPerson } = useCurrentBilikPerson();

  const handleCancelSchedule = (proViewId: number) => {
    updateProView([proViewId], {
      dateArchiveScheduled: null,
      archiveReason: null as any,
    }).catch((error) => {
      ToastError(
        'Erreur',
        `Impossible de déprogrammer l'archivage de la fiche`,
      );
      throw error;
    });
    refetch();
    ToastSuccess('Succès', 'Archivage déprogrammé');
  };

  const onSubmit = useCallback(
    async (values: ProFormArchiveFields): Promise<void> => {
      const errors = await validateForm({
        ...proFormValues,
        status: 'archived',
      });

      const proViewIds = values.proViewIds;
      const dateArchiveScheduled = values.dateArchiveScheduled;
      const archiveReason = values.archiveReason;

      if (equals(errors, {})) {
        // Update proView with archiveReason and optionally dateArchiveScheduled
        await updateProView(proViewIds, {
          archiveReason: archiveReason,
          ...(dateArchiveScheduled && {
            dateArchiveScheduled: dateArchiveScheduled,
          }),
        }).catch((error) => {
          ToastError(
            'Erreur',
            dateArchiveScheduled
              ? `Impossible de programmer l'archivage de la fiche`
              : "Impossible de sauvegarder la raison de l'archivage",
          );
          throw error;
        });

        if (!dateArchiveScheduled) {
          await apiClient
            .post(`/form/pro-view/archive`, {
              proViewIds,
              dateArchiveScheduled,
            })
            .catch((error) => {
              ToastError('Erreur', "Erreur lors de l'archivage (legacy)");
              throw error;
            });

          // Update the proView date archived to prevent the proView from being published again on handleSubmit
          setFieldValue('status', 'archived');
        }

        // TODO : complete archive mutation, for now it only send mail to pro
        await archiveViews({
          body: {
            viewIds: proViewIds,
            date: dateArchiveScheduled
              ? format(dateArchiveScheduled, 'yyyy-MM-dd')
              : undefined,
          },
        }).catch((error) => {
          ToastError('Erreur', "Erreur lors de l'archivage (v2)");
          throw error;
        });

        if (dateArchiveScheduled) {
          refetch();
          ToastSuccess('Succès', 'Archivage programmé');
          onClose?.();
        } else {
          setTimeout(() => handleSubmit());
        }
      } else {
        const errorValues = Object.values(flattenObject(errors)) as string[];
        const errorMsg = getFormErrorMessage(errorValues);

        ToastError(
          dateArchiveScheduled
            ? "Impossible de programmer l'archivage de la fiche"
            : "Impossible d'archiver la fiche",
          errorMsg,
        );
      }
    },
    [proViews, currentBilikPerson, proFormValues, refetch, onClose],
  );

  const initialValues: ProFormArchiveFields = useMemo(
    () => ({
      proViewIds: [proViewId],
      dateArchiveScheduled: undefined,
      archiveReason: '',
    }),
    [proViews],
  );

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        proViewIds: Yup.array().min(1, 'Fiche requise !'),
        archiveReason: Yup.string().required("Raison de l'archivage requise !"),
      })}
    >
      <ProFormArchiveModalView
        onClose={onClose}
        proViews={proViews}
        proViewId={proViewId}
        onCancelSchedule={handleCancelSchedule}
        {...rest}
      />
    </Formik>
  );
};

export default ProFormArchiveModalContainer;
